import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  UserCheck, Plus, Pencil, Trash2, Loader2, Search,
  Phone, Mail, KeyRound, Eye, EyeOff, Store,
} from "lucide-react";
import axios from "axios";

const API = "/api";

const PinInput = ({ id, name, value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id} name={name}
        type={show ? "text" : "password"}
        value={value} onChange={onChange}
        placeholder={placeholder}
        maxLength={6}
        inputMode="numeric"
        pattern="[0-9]*"
        className="bg-white border-gray-300 pr-10"
        required
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

const DeliveryManagersPage = () => {
  const [managers, setManagers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", pin: "", confirm_pin: "", store_id: "",
  });
  const [pinData, setPinData] = useState({ pin: "", confirm_pin: "" });

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("ecomdemo_token")}` },
  });

  const fetchManagers = async () => {
    try {
      const res = await axios.get(`${API}/admin/delivery-managers`, authHeaders());
      setManagers(res.data);
    } catch {
      toast.error("Failed to fetch delivery managers");
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API}/stores`, authHeaders());
      setStores(res.data);
    } catch {
      // non-critical, swallow silently
    }
  };

  useEffect(() => { fetchManagers(); fetchStores(); }, []);

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", pin: "", confirm_pin: "", store_id: "" });
    setSelected(null);
  };

  const openAdd = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (mgr) => {
    setSelected(mgr);
    setFormData({ name: mgr.name, email: mgr.email, phone: mgr.phone.replace(/^\+91/, ""), pin: "", confirm_pin: "", store_id: mgr.store_id || "" });
    setDialogOpen(true);
  };

  const openDelete = (mgr) => { setSelected(mgr); setDeleteDialogOpen(true); };

  const openChangePIN = (mgr) => {
    setSelected(mgr);
    setPinData({ pin: "", confirm_pin: "" });
    setPinDialogOpen(true);
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (selected) {
        const { pin, confirm_pin, phone, ...rest } = formData;
        await axios.put(`${API}/admin/delivery-managers/${selected.id}`, { ...rest, phone: `+91${phone}` }, authHeaders());
        toast.success("Delivery manager updated");
      } else {
        await axios.post(`${API}/admin/delivery-managers`, { ...formData, phone: `+91${formData.phone}` }, authHeaders());
        toast.success("Delivery manager added");
      }
      setDialogOpen(false);
      resetForm();
      fetchManagers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/admin/delivery-managers/${selected.id}`, authHeaders());
      toast.success("Delivery manager deleted");
      setDeleteDialogOpen(false);
      setSelected(null);
      fetchManagers();
    } catch {
      toast.error("Failed to delete delivery manager");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePIN = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/admin/delivery-managers/${selected.id}/change-pin`,
        pinData,
        authHeaders()
      );
      toast.success("PIN updated successfully");
      setPinDialogOpen(false);
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update PIN");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = managers.filter(
    (m) =>
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.phone.includes(searchTerm)
  );

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <UserCheck className="text-[#6C42F5]" size={28} />
            Delivery Managers
          </h1>
          <p className="text-gray-600 mt-1">Manage your delivery team</p>
        </div>
        <Button onClick={openAdd} className="bg-[#6C42F5] text-white hover:bg-[#8C6CFF] rounded-lg">
          <Plus size={20} className="mr-2" />
          Add Manager
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search by name, email or phone…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-gray-300"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#6C42F5]" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm ? "No managers found" : "No delivery managers added yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700">Name</TableHead>
                  <TableHead className="font-bold text-gray-700">Email</TableHead>
                  <TableHead className="font-bold text-gray-700">Phone</TableHead>
                  <TableHead className="font-bold text-gray-700">Store</TableHead>
                  <TableHead className="font-bold text-gray-700">Added On</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((mgr) => (
                  <TableRow key={mgr.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium text-gray-900">{mgr.name}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-gray-600">
                        <Mail size={14} className="text-gray-400" />
                        {mgr.email}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-gray-600">
                        <Phone size={14} className="text-gray-400" />
                        {mgr.phone}
                      </span>
                    </TableCell>
                    <TableCell>
                      {mgr.store_name ? (
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <Store size={14} className="text-gray-400" />
                          {mgr.store_name}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm italic">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">{formatDate(mgr.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => openChangePIN(mgr)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 gap-1"
                          title="Change PIN"
                        >
                          <KeyRound size={15} />
                          <span className="text-xs">PIN</span>
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => openEdit(mgr)}
                          className="text-gray-600 hover:text-[#6C42F5]"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => openDelete(mgr)}
                          className="text-gray-600 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {selected ? "Edit Delivery Manager" : "Add Delivery Manager"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name" className="text-gray-700 font-medium">Name</Label>
              <Input
                id="name" name="name" value={formData.name} onChange={handleChange}
                required placeholder="Full name" className="mt-1 bg-white border-gray-300"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
              <Input
                id="email" name="email" type="email" value={formData.email} onChange={handleChange}
                required placeholder="email@example.com" className="mt-1 bg-white border-gray-300"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-gray-700 font-medium">Phone</Label>
              <div className="flex mt-1">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium select-none">
                  +91
                </span>
                <Input
                  id="phone" name="phone" type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setFormData({ ...formData, phone: digits });
                  }}
                  required
                  placeholder="98765 43210"
                  maxLength={10}
                  className="rounded-l-none bg-white border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Store</Label>
              <Select
                value={formData.store_id}
                onValueChange={(val) => setFormData({ ...formData, store_id: val === "__none__" ? "" : val })}
              >
                <SelectTrigger className="mt-1 bg-white border-gray-300">
                  <SelectValue placeholder="Select a store (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No store assigned</SelectItem>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* PIN fields only for new manager */}
            {!selected && (
              <>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-3 flex items-center gap-1.5">
                    <KeyRound size={13} /> Set a 4–6 digit PIN for app login
                  </p>
                  <Label htmlFor="pin" className="text-gray-700 font-medium">PIN</Label>
                  <div className="mt-1">
                    <PinInput
                      id="pin" name="pin" value={formData.pin}
                      onChange={handleChange} placeholder="Enter 4–6 digit PIN"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm_pin" className="text-gray-700 font-medium">Confirm PIN</Label>
                  <div className="mt-1">
                    <PinInput
                      id="confirm_pin" name="confirm_pin" value={formData.confirm_pin}
                      onChange={handleChange} placeholder="Re-enter PIN"
                    />
                  </div>
                </div>
              </>
            )}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-700">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#6C42F5] text-white hover:bg-[#8C6CFF]">
                {submitting
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{selected ? "Updating…" : "Adding…"}</>
                  : (selected ? "Update" : "Add Manager")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change PIN Dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="bg-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <KeyRound size={20} className="text-amber-500" />
              Change PIN
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mt-1">
            Setting new PIN for <span className="font-semibold text-gray-800">{selected?.name}</span>
          </p>
          <form onSubmit={handleChangePIN} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="new_pin" className="text-gray-700 font-medium">New PIN</Label>
              <div className="mt-1">
                <PinInput
                  id="new_pin" name="pin"
                  value={pinData.pin}
                  onChange={(e) => setPinData({ ...pinData, pin: e.target.value })}
                  placeholder="Enter 4–6 digit PIN"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="new_confirm_pin" className="text-gray-700 font-medium">Confirm New PIN</Label>
              <div className="mt-1">
                <PinInput
                  id="new_confirm_pin" name="confirm_pin"
                  value={pinData.confirm_pin}
                  onChange={(e) => setPinData({ ...pinData, confirm_pin: e.target.value })}
                  placeholder="Re-enter new PIN"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setPinDialogOpen(false)} className="border-gray-300 text-gray-700">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-amber-500 text-white hover:bg-amber-600">
                {submitting
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                  : "Update PIN"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Delete Delivery Manager</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Are you sure you want to remove <span className="font-semibold">{selected?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button onClick={handleDelete} disabled={submitting} className="bg-red-500 text-white hover:bg-red-600">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryManagersPage;
